import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export const AuthModal = ({ isOpen, onClose, mode, setMode }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", name: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        await register(formData.email, formData.password, formData.name);
        toast.success("Account created successfully!");
      }
      onClose();
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-hsl-surface border border-white/10 sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase tracking-tight">
            {mode === "login" ? "Welcome Back" : "Join HSL"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name" className="text-zinc-400 text-sm">Name</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-hsl mt-1"
                placeholder="Your name"
                required
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <Label htmlFor="email" className="text-zinc-400 text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input-hsl mt-1"
              placeholder="you@example.com"
              required
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-zinc-400 text-sm">Password</Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="input-hsl mt-1"
              placeholder="••••••••"
              required
              minLength={6}
              data-testid="auth-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-primary-glow py-3 rounded-sm"
            data-testid="auth-submit-btn"
          >
            {loading ? "Loading..." : mode === "login" ? "Sign In" : "Create Account"}
          </Button>
          <p className="text-center text-zinc-500 text-sm">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-hsl-primary hover:underline"
              data-testid="auth-switch-mode"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Navbar = ({ onLoginClick, onRegisterClick }) => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-blur bg-hsl-bg/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/hsl-logo.png" alt="HSL.bet" className="h-12 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/dashboard" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide ${isActive('/dashboard') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-hsl-primary'}`}
              data-testid="nav-picks"
            >
              Picks
            </Link>
            <Link 
              to="/track-record" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide ${isActive('/track-record') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-hsl-primary'}`}
              data-testid="nav-track-record"
            >
              Results
            </Link>
            <Link 
              to="/pricing" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide ${isActive('/pricing') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-hsl-primary'}`}
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
            <Link 
              to="/hammer-collection" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide flex items-center gap-1 ${isActive('/hammer-collection') ? 'text-yellow-400' : 'text-zinc-400 hover:text-yellow-400'}`}
              data-testid="nav-hammer-collection"
            >
              <span>🔨</span> Hammers
            </Link>
            <Link 
              to="/masterclass" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide flex items-center gap-1 ${isActive('/masterclass') ? 'text-yellow-400' : 'text-zinc-400 hover:text-yellow-400'}`}
              data-testid="nav-masterclass"
            >
              <span>🏆</span> Masterclass
            </Link>
            <Link 
              to="/hammer-talk" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide ${isActive('/hammer-talk') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-hsl-primary'}`}
              data-testid="nav-hammer-talk"
            >
              Hammer Talk
            </Link>
            <Link 
              to="/road-to-100k" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide flex items-center gap-1 ${isActive('/road-to-100k') ? 'text-green-400' : 'text-zinc-400 hover:text-green-400'}`}
              data-testid="nav-road-to-100k"
            >
              Road to $100K
            </Link>
            <Link 
              to="/updates" 
              className={`transition-colors text-sm font-semibold uppercase tracking-wide ${isActive('/updates') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-hsl-primary'}`}
              data-testid="nav-updates"
            >
              Updates
            </Link>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className={`transition-colors text-sm font-semibold uppercase tracking-wide ${isActive('/admin') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-hsl-primary'}`}
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </div>

          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <span className="text-zinc-400 text-sm">Welcome, <span className="text-white">{user.name}</span></span>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-zinc-400 hover:text-white"
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={onLoginClick}
                  className="text-zinc-400 hover:text-white"
                  data-testid="login-btn"
                >
                  Login
                </Button>
                <Button
                  onClick={onRegisterClick}
                  className="btn-primary-glow px-6 py-2 rounded-sm"
                  data-testid="signup-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/5">
            <div className="flex flex-col gap-4">
              <Link to="/dashboard" className={`py-2 ${isActive('/dashboard') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-white'}`} onClick={() => setMobileMenuOpen(false)}>Picks</Link>
              <Link to="/track-record" className={`py-2 ${isActive('/track-record') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-white'}`} onClick={() => setMobileMenuOpen(false)}>Results</Link>
              <Link to="/pricing" className={`py-2 ${isActive('/pricing') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-white'}`} onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link to="/hammer-collection" className={`py-2 flex items-center gap-1 ${isActive('/hammer-collection') ? 'text-yellow-400' : 'text-zinc-400 hover:text-yellow-400'}`} onClick={() => setMobileMenuOpen(false)}>🔨 Hammers</Link>
              <Link to="/masterclass" className={`py-2 flex items-center gap-1 ${isActive('/masterclass') ? 'text-yellow-400' : 'text-zinc-400 hover:text-yellow-400'}`} onClick={() => setMobileMenuOpen(false)}>🏆 Masterclass</Link>
              <Link to="/hammer-talk" className={`py-2 ${isActive('/hammer-talk') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-white'}`} onClick={() => setMobileMenuOpen(false)}>Hammer Talk</Link>
              <Link to="/road-to-100k" className={`py-2 ${isActive('/road-to-100k') ? 'text-green-400' : 'text-zinc-400 hover:text-green-400'}`} onClick={() => setMobileMenuOpen(false)}>Road to $100K</Link>
              <Link to="/updates" className={`py-2 ${isActive('/updates') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-white'}`} onClick={() => setMobileMenuOpen(false)}>Updates</Link>
              {user?.is_admin && (
                <Link to="/admin" className={`py-2 ${isActive('/admin') ? 'text-hsl-primary' : 'text-zinc-400 hover:text-white'}`} onClick={() => setMobileMenuOpen(false)}>Admin</Link>
              )}
              {user ? (
                <Button variant="ghost" onClick={() => { logout(); setMobileMenuOpen(false); }} className="justify-start px-0">Logout</Button>
              ) : (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => { onLoginClick(); setMobileMenuOpen(false); }} className="flex-1">Login</Button>
                  <Button onClick={() => { onRegisterClick(); setMobileMenuOpen(false); }} className="flex-1 btn-primary-glow">Sign Up</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
